/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import { CardSkills, ProductCard, breakpoints } from '@coursera/cds-core';
import type {
  ProductCard as ProductCardTrackingData,
  Product as ProductTrackingData,
  ProductType,
  Recommender,
} from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { getCreditType, shouldShowCreditBadge } from 'bundles/product-card/components/LearningProductCardBadge';
import LearningProductCardFooter from 'bundles/product-card/components/LearningProductCardFooter';
import { getMarketingStyledPillTag } from 'bundles/product-card/components/utils/getMarketingTagStyle';
import { getMarketingStatusTagForSlug } from 'bundles/product-card/components/utils/marketingTagUtils';
import {
  capitalizeAndTranslateEachWordInEntityType,
  eventingV3ProductType,
  extractEventId,
  saveProductCardRecentlyViewed,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import type { AutocompleteProductCardData } from 'bundles/product-card/components/utils/productCardV2Utils';
import { PRODUCT_CARD_EXPERIMENT_VARIANT } from 'bundles/product-card/constants';
import { useShouldShowNewMerchPills } from 'bundles/product-card/hooks/useShouldShowNewMerchPills';
import { BADGE_NAMES } from 'bundles/product-card/utils/getProductCardStatusTags';
import { isEnterprisePathname } from 'bundles/product-card/utils/isEnterprisePathname';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import { getUpsellCopy } from 'bundles/stackability-common/stackability-common-index';

/**
 * CDS-internal markup for the partner block inside `ProductCard`. CDS exposes no
 * partner click API, so partner clicks are detected by event delegation on this
 * selector (same coupling as `.cds-ProductCard-partnerLogos` in styles below).
 */
export const PRODUCT_CARD_PARTNER_INFO_SELECTOR = '.cds-ProductCard-partnerInfo';

const styles = {
  leftAlignProductCardTags: css`
    ${breakpoints.up('md')} {
      .cds-ProductCard-listCard {
        .cds-ProductCard-statusTags {
          position: absolute;
          left: 0;
          right: unset;
          top: var(--cds-spacing-100);
          padding-left: var(--cds-spacing-100);
          max-width: 298px;
          z-index: 1;
        }
      }
    }
  `,

  rightAlignProductCardTags: css`
    ${breakpoints.up('sm')} {
      .cds-ProductCard-statusTagsOverlay {
        left: unset !important;
        right: 0;
      }
    }
  `,

  cardContainer: css`
    min-height: 200px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: pointer;

    .cds-ProductCard-partnerLogos img {
      vertical-align: unset;
    }

    .cds-CommonCard-previewImage {
      position: relative;
    }

    & div.cds-ProductCard-content div.cds-ProductCard-footer {
      justify-content: flex-end;
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-base.cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,

  clickablePartners: css`
    .cds-ProductCard-partnerInfo {
      cursor: pointer;
    }

    .cds-ProductCard-partnerInfo:hover .cds-ProductCard-partnerNames {
      text-decoration: underline;
      color: var(--cds-color-interactive-primary-hover);
    }
  `,
};

type Tag = string | React.ReactElement;
type LearningProduct = (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];

type Props = {
  adminContent?: React.ReactNode;
  customMetadata?: string[];
  customOnClick?: () => void;
  /** When provided, LearningProductCard records this click to Recently Viewed (autocomplete) itself. */
  autocompleteProductCardData?: AutocompleteProductCardData;
  ImageComponent?: React.ReactNode;
  name: string;
  linkProps?: { href?: string; target?: string; rel?: string; component?: React.ElementType };
  titleComponent?: 'h3' | 'h4';
  partners?: { id: string; logo?: string; name: string }[];
  skills?: string[];
  bodyContent?: React.ReactNode;
  numProductRatings?: number;
  avgProductRating?: number;
  productType?: LearningProduct;
  productDifficultyLevel?: string;
  productDuration?: string;
  statusTags?: { tags: Tag[]; tagNames?: string[] };
  preBadgeContent?: React.ReactNode;
  badgeConfig?: {
    hideCreditBadge?: boolean;
    productType?: LearningProduct;
    isPathwayContent?: boolean;
  };
  statusTagConfig?: {
    hideCreditStatusTag?: boolean;
    hideAllStatusTags?: boolean;
  };
  postBadgeContent?: React.ReactNode;
  variant?: 'list' | 'grid';
  size?: 'compact' | 'standard';
  /** Keeps the list card's square thumbnail at large breakpoints instead of CDS's 16:9 preview. */
  alwaysSmallImage?: boolean;
  id?: string;
  slug?: string;
  cardIndex?: number;
  indexName?: string;
  recommender?: Recommender;
  trackingProps?: {
    trackedLinkProps?: {
      trackingName: string;
      withVisibilityTracking: boolean;
      requireFullyVisible: boolean;
      data: Record<string, unknown>;
      refAlt?: React.RefObject<HTMLAnchorElement>;
    };
    additionalProductCardTrackingData?: Partial<ProductCardTrackingData>;
    additionalProductTrackingData?: Partial<ProductTrackingData>;
    additionalV3TrackingData?: Record<string, unknown>;
  };
  isMetadataAboveProductRating?: boolean;
  buttons?: Array<React.ReactElement>;
  subscriptionFooterTag?: string;
  /** Pass-through to LearningProductCardFooter — renders badge after metadata, just above buttons. */
  badgeAfterMetadata?: boolean;
  /** Search-only top match label injected into redesigned footer pill tags. */
  topMatchStatusTagLabel?: string;
  /** Whether the view_product_card visibility tracker requires the card to be fully visible. Defaults to true. */
  requireFullyVisibleCardTracking?: boolean;
  /**
   * Fired when the user clicks the partner name. Receives the partner display name.
   * Only active when exactly one partner is shown (CDS renders "Multiple educators" otherwise).
   */
  onPartnerClick?: (partnerName: string) => void;
};

const LearningProductCard = ({
  adminContent,
  customMetadata,
  customOnClick,
  autocompleteProductCardData,
  ImageComponent,
  name,
  titleComponent,
  linkProps,
  partners,
  skills,
  bodyContent,
  numProductRatings,
  avgProductRating,
  productType,
  productDifficultyLevel,
  productDuration,
  statusTags,
  preBadgeContent,
  badgeConfig,
  statusTagConfig,
  postBadgeContent,
  variant = 'grid',
  size = 'standard',
  alwaysSmallImage = false,
  id,
  slug,
  cardIndex,
  indexName,
  recommender,
  trackingProps,
  isMetadataAboveProductRating = false,
  buttons,
  subscriptionFooterTag,
  badgeAfterMetadata = false,
  topMatchStatusTagLabel,
  requireFullyVisibleCardTracking = true,
  onPartnerClick,
}: Props) => {
  const track = useTracker();

  const singlePartnerName = partners?.length === 1 ? partners[0]?.name : undefined;
  const isPartnerClickable = Boolean(onPartnerClick) && Boolean(singlePartnerName);

  const getTrackingProductId = (productId: string | undefined): string => {
    if (typeof productId !== 'string') {
      return '';
    }
    if (productId.startsWith('child~')) {
      return productId; // Preserve "child~" prefix for product IDs in tracking events
    }
    return extractEventId(productId) || '';
  };

  const slugMarketingTag = slug ? getMarketingStatusTagForSlug(slug, { includeRecentlyUpdated: true }) : undefined;
  const isRecentlyUpdatedCard = slugMarketingTag?.category === 'recently-updated';
  // Only read the experiment when the pill can actually render, so exposure isn't logged for cards that
  // can never show it (sponsored, hidden or suppressed tag area, Enterprise pages, or the two-pill cap
  const location = useLocation();
  const canShowMarketingPill =
    !statusTagConfig?.hideAllStatusTags &&
    !isMetadataAboveProductRating &&
    !isEnterprisePathname(location.pathname) &&
    !(topMatchStatusTagLabel && subscriptionFooterTag) &&
    !statusTags?.tagNames?.includes(BADGE_NAMES.SPONSORED);
  const shouldShowNewMerchPills = useShouldShowNewMerchPills(isRecentlyUpdatedCard && canShowMarketingPill);
  const marketingTag = isRecentlyUpdatedCard && !shouldShowNewMerchPills ? undefined : slugMarketingTag;
  // A slug maps to at most one marketing category, so there is only ever one pill here. The
  // styled renderer re-uses marketingTag.tag as its slot key and must never be prepended to
  // tagNames as a second entry, or the pill would render twice.
  const styledPillTag = marketingTag ? getMarketingStyledPillTag(marketingTag) : undefined;
  const styledPillTags = styledPillTag ? [styledPillTag] : [];
  const shouldInlineTopMatchInStatusTags = Boolean(topMatchStatusTagLabel);

  const cardMetadata =
    customMetadata ||
    [productDifficultyLevel, capitalizeAndTranslateEachWordInEntityType(productType), productDuration].filter(Boolean);

  // Rendering list cards with even a single status tag can cause styling issues with longer-typed
  // languages for desktop widths(see https://coursera.atlassian.net/browse/MERCH-1660). As a
  // workaround, we left-align tags and assign a max-width slightly less than the preview image.
  const listCardWithStatusTags = variant === 'list' && statusTags?.tags?.length && statusTags?.tags?.length >= 1;

  const combinedBadge = (
    <>
      {preBadgeContent}
      {!shouldInlineTopMatchInStatusTags && postBadgeContent}
    </>
  );

  const creditBadgePillTag =
    !statusTagConfig?.hideCreditStatusTag &&
    shouldShowCreditBadge(badgeConfig?.productType) &&
    getCreditType(badgeConfig?.productType).label;

  const getStatusTagsForFooter = () => {
    if (statusTagConfig?.hideAllStatusTags) {
      return undefined;
    }

    let tagNames = statusTags?.tagNames ?? [];

    if (marketingTag) {
      // Compliance carve-out: on a sponsored card "Sponsored" REPLACES the marketing tag.
      // We use marketingTag.tag (already translated) for the non-sponsored case.
      const hasSponsored = statusTags?.tagNames?.includes(BADGE_NAMES.SPONSORED);
      tagNames = hasSponsored ? [BADGE_NAMES.SPONSORED] : [marketingTag.tag, ...tagNames];
    } else if (badgeConfig?.isPathwayContent) {
      tagNames = [...tagNames, getUpsellCopy().homepagesNavXdp.buildTowardADegree];
    } else if (creditBadgePillTag) {
      tagNames = [...tagNames, creditBadgePillTag];
    }

    if (subscriptionFooterTag) {
      tagNames = [subscriptionFooterTag, ...tagNames];
    }

    if (shouldInlineTopMatchInStatusTags && topMatchStatusTagLabel) {
      const capped = [topMatchStatusTagLabel, ...tagNames].slice(0, 2);
      // The 2-pill cap must never evict the mandatory Sponsored disclosure.
      if (tagNames.includes(BADGE_NAMES.SPONSORED) && !capped.includes(BADGE_NAMES.SPONSORED)) {
        return [topMatchStatusTagLabel, BADGE_NAMES.SPONSORED];
      }
      return capped;
    }

    return tagNames;
  };

  const trackingData: EventData<'click_product_card'> = {
    product: {
      id: getTrackingProductId(id),
      name,
      slug: slug ?? linkProps?.href ?? '',
      type: eventingV3ProductType(productType) as ProductType,
      ...trackingProps?.additionalProductTrackingData,
    },
    productCard: {
      ...(typeof trackingProps?.trackedLinkProps?.data.productCard === 'object'
        ? trackingProps?.trackedLinkProps?.data.productCard
        : {}),
      index: cardIndex ?? 0,
      ...(indexName && { algoliaIndexName: indexName }),
      ...(recommender && { recommender }),
      tags: getStatusTagsForFooter(),
      ...(trackingProps?.additionalProductCardTrackingData?.pageConfigComponent && {
        pageConfigComponent: trackingProps?.additionalProductCardTrackingData?.pageConfigComponent,
      }),
    },
    ...trackingProps?.additionalV3TrackingData,
  };

  const trackedCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    trackingData,
    [],
    { fullyVisible: requireFullyVisibleCardTracking, initialDelay: 1000 }
  );

  return (
    <div
      css={[
        styles.cardContainer,
        listCardWithStatusTags ? styles.leftAlignProductCardTags : styles.rightAlignProductCardTags,
        variant === 'list' ? styles.listCardContainer : null,
        isPartnerClickable ? styles.clickablePartners : null,
      ]}
      data-testid="product-card-cds"
    >
      <ProductCard
        experimentVariant={PRODUCT_CARD_EXPERIMENT_VARIANT}
        onClick={(event) => {
          if (
            isPartnerClickable &&
            singlePartnerName &&
            event.target instanceof Element &&
            event.target.closest(PRODUCT_CARD_PARTNER_INFO_SELECTOR)
          ) {
            // Partner click is a filter action, not a card click: skip card tracking,
            // Recently Viewed and navigation.
            event.preventDefault();
            event.stopPropagation();
            onPartnerClick?.(singlePartnerName);
            return;
          }
          track('click_product_card', trackingData);
          if (linkProps?.href) {
            // Only record Recently Viewed when the click actually navigates to the product's page —
            // customOnClick below covers non-navigating interactions (e.g. admin pickers, in-place modals).
            if (autocompleteProductCardData && name) {
              saveProductCardRecentlyViewed(
                { id, name, slug, path: linkProps.href, productType, isPathwayProduct: badgeConfig?.isPathwayContent },
                autocompleteProductCardData
              );
            }
            return;
          }
          if (customOnClick) {
            event.preventDefault();
            event.stopPropagation();
            customOnClick();
          }
        }}
        ref={trackedCardRef}
        renderPreviewImage={() => ImageComponent}
        title={{
          name,
          linkProps,
          component: titleComponent,
          customLinkProps: trackingProps?.trackedLinkProps,
        }}
        partners={partners}
        body={
          (skills && skills.length > 0) || bodyContent ? (
            <>
              {skills && skills.length > 0 && <CardSkills skills={skills} />}
              {bodyContent}
            </>
          ) : undefined
        }
        footer={
          <LearningProductCardFooter
            isMetadataAboveProductRating={isMetadataAboveProductRating}
            cardMetadata={cardMetadata}
            avgProductRating={avgProductRating}
            numProductRatings={numProductRatings}
            badge={combinedBadge}
            shouldUseColorCodedPillTags
            statusTagNames={getStatusTagsForFooter()}
            buttons={buttons}
            productCardVariant={variant}
            badgeAfterMetadata={badgeAfterMetadata}
            topMatchLabel={shouldInlineTopMatchInStatusTags ? topMatchStatusTagLabel : undefined}
            styledPillTags={styledPillTags}
          />
        }
        variant={variant}
        size={size}
        alwaysSmallImage={alwaysSmallImage}
        productType={productType || ''}
        adminContent={adminContent}
      />
    </div>
  );
};

export default LearningProductCard;
